import { Injectable, Signal, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Observable,
  Subject,
  catchError,
  defer,
  from,
  forkJoin,
  map,
  mergeMap,
  of,
  switchMap,
  takeUntil,
  tap,
  throwError,
  toArray,
} from 'rxjs';
import {
  CollectionIntegrationAuthorization,
  CollectionRevisionValidationFileDto,
  CollectionRevisionValidationResponse,
  CollectionWorkflowDto,
  StageTargetFileResponse,
  TargetFileIdRequest,
  VersionedCollectionRevisionFileSetCommit,
  VersionedCollectionRevisionTargetFileSet,
} from '../interfaces/collection-revision.interface';
import {
  CollectionRevisionPreconditionConflict,
  CollectionRevisionService,
} from '../services/collection-revision.service';
import { CollectionRevisionTargetRouteIdentity } from './collection-revision-route';
import {
  CollectionRevisionFileEditorSnapshot,
  CollectionRevisionFileEditorFailure,
  CollectionRevisionFileEditorStateMachine,
  CollectionRevisionNavigationDecision,
  CollectionRevisionNavigationDirection,
  CollectionRevisionNavigationIntent,
  CollectionRevisionPrimaryActionDecision,
  FrozenCollectionRevisionFileIntent,
} from './collection-revision-file-editor';
import { mergeFileIds } from './collection-revision-file-draft';
import { CollectionRevisionLifecycleCoordinator } from './collection-revision-lifecycle-coordinator';

export interface CollectionRevisionFileEditorCommitResult {
  readonly direction: CollectionRevisionNavigationIntent;
  readonly identity: CollectionRevisionTargetRouteIdentity;
  readonly workflow: CollectionWorkflowDto;
  readonly authorization: CollectionIntegrationAuthorization | null;
}

export class CollectionRevisionFileValidationError extends Error {
  constructor(readonly validation: CollectionRevisionValidationResponse) {
    super('One or more collection revision files require attention.');
    this.name = 'CollectionRevisionFileValidationError';
  }
}

class CollectionRevisionFileUploadError extends Error {
  constructor(
    readonly failures: readonly {
      readonly clientId: string;
      readonly fileName: string;
      readonly error: unknown;
    }[],
  ) {
    super('One or more collection revision files failed to upload.');
    this.name = 'CollectionRevisionFileUploadError';
  }
}

@Injectable()
export class CollectionRevisionFileEditorController {
  private readonly revisionService = inject(CollectionRevisionService);
  private readonly lifecycleCoordinator = inject(
    CollectionRevisionLifecycleCoordinator,
  );
  private machine = new CollectionRevisionFileEditorStateMachine();
  private readonly stateSignal = signal(this.machine.snapshot());
  private readonly reset$ = new Subject<void>();
  private identity: CollectionRevisionTargetRouteIdentity | null = null;
  private integrationEnabled = false;
  private localSequence = 0;
  private readonly stagedByClientId = new Map<
    string,
    StageTargetFileResponse
  >();
  private conflictIntent: FrozenCollectionRevisionFileIntent | null = null;

  readonly state: Signal<CollectionRevisionFileEditorSnapshot> =
    this.stateSignal.asReadonly();

  reset(): void {
    this.reset$.next();
    this.machine = new CollectionRevisionFileEditorStateMachine();
    this.identity = null;
    this.integrationEnabled = false;
    this.localSequence = 0;
    this.stagedByClientId.clear();
    this.conflictIntent = null;
    this.publish();
  }

  hydrate(
    identity: CollectionRevisionTargetRouteIdentity,
    fileSet: VersionedCollectionRevisionTargetFileSet,
    integrationEnabled: boolean,
  ): void {
    const captured = freezeIdentity(identity);
    if (
      fileSet.targetFileSet.collection_id !== captured.collectionId ||
      fileSet.targetFileSet.revision_id !== captured.revisionId ||
      fileSet.targetFileSet.target_id !== captured.targetId
    ) {
      throw new Error(
        'The target file set does not identify the file editor route.',
      );
    }
    this.identity = captured;
    this.integrationEnabled = integrationEnabled;
    this.stagedByClientId.clear();
    this.conflictIntent = null;
    this.machine.hydrate(fileSet);
    this.publish();
  }

  addFiles(files: readonly File[]): boolean {
    if (this.state().busy) return false;
    let changed = false;
    for (const file of files) {
      changed =
        this.machine.add({
          clientId: `file-${++this.localSequence}`,
          file,
        }) || changed;
    }
    this.publish();
    return changed;
  }

  removeFile(key: string): boolean {
    const changed = this.machine.remove(key);
    this.publish();
    return changed;
  }

  fileIdForRow(
    row: CollectionRevisionFileEditorSnapshot['rows'][number],
  ): string | null {
    if (row.origin === 'PERSISTED') return row.fileId;
    return this.stagedByClientId.get(row.clientId)?.file_id ?? null;
  }

  resolveRequiredInput(fileId: string, key: string, value: string): boolean {
    if (this.state().busy) return false;
    const changed = this.machine.resolveRequiredInput(fileId, key, value);
    this.publish();
    return changed;
  }

  retryFailedUpload(clientId: string): Observable<boolean> {
    return defer(() => {
      const identity = this.requireIdentity();
      const exactClientId = requireNonEmpty(clientId, 'clientId');
      const row = this.state().rows.find(
        (candidate) =>
          candidate.origin === 'LOCAL' && candidate.clientId === exactClientId,
      );
      if (
        !row ||
        row.origin !== 'LOCAL' ||
        this.state().uploadStateByClientId[exactClientId] !== 'UPLOAD_FAILED'
      ) {
        throw new Error('Retry requires a failed local upload.');
      }
      return this.uploadLocalFile(identity, exactClientId, row.file).pipe(
        map(() =>
          this.state().rows
            .filter((candidate) => candidate.origin === 'LOCAL')
            .every(
              (candidate) =>
                candidate.origin === 'LOCAL' &&
                this.stagedByClientId.has(candidate.clientId),
            ),
        ),
        catchError((error: unknown) => {
          this.machine.setLocalUploadState(exactClientId, 'UPLOAD_FAILED');
          this.publish();
          return throwError(() => error);
        }),
      );
    }).pipe(takeUntil(this.reset$));
  }

  revalidateRequiredInput(
    fileId: string,
  ): Observable<CollectionRevisionValidationFileDto> {
    return defer(() => {
      if (this.state().busy) {
        throw new Error('Cannot revalidate a file during an active commit.');
      }
      const identity = this.requireIdentity();
      const exactFileId = requireNonEmpty(fileId, 'fileId');
      const request = this.toFileSetRequest([exactFileId]);
      return this.revisionService
        .validateTargetFiles(
          identity.collectionId,
          identity.revisionId,
          identity.targetId,
          request,
        )
        .pipe(
          map((validation) => {
            const file = validation.files[0];
            if (
              validation.files.length !== 1 ||
              !file ||
              file.file_id !== exactFileId ||
              !sameResolvedInputs(
                request.files[0].resolved_inputs,
                file.resolved_inputs,
              )
            ) {
              throw new Error(
                'Required-input revalidation returned a different file projection.',
              );
            }
            this.machine.recordFileValidation(file);
            this.publish();
            return file;
          }),
        );
    }).pipe(takeUntil(this.reset$));
  }

  discardValidationFiles(fileIds: readonly string[]): Observable<void> {
    return defer(() => {
      const identity = this.requireIdentity();
      const exactFileIds = [
        ...new Set(fileIds.map((fileId) => requireNonEmpty(fileId, 'fileId'))),
      ];
      const permanentRejects = exactFileIds.filter((fileId) => {
        const validation = this.state().validationByFile[fileId];
        return (
          validation &&
          !validation.usable &&
          validation.required_inputs.length === 0
        );
      });
      if (permanentRejects.length !== exactFileIds.length) {
        throw new Error('Only permanently rejected files can be discarded.');
      }
      if (permanentRejects.length === 0) return of(undefined);

      return forkJoin(
        permanentRejects.map((fileId) =>
          this.revisionService.discardTargetFile(
            identity.collectionId,
            identity.revisionId,
            identity.targetId,
            fileId,
          ),
        ),
      ).pipe(
        tap(() => {
          for (const fileId of permanentRejects) {
            const staged = [...this.stagedByClientId.entries()].find(
              ([, response]) => response.file_id === fileId,
            );
            if (staged) {
              this.machine.remove(`local:${staged[0]}`);
              this.stagedByClientId.delete(staged[0]);
            } else {
              this.machine.remove(`persisted:${fileId}`);
            }
          }
          this.machine.clearValidationFiles(permanentRejects);
          this.publish();
        }),
        map(() => undefined),
      );
    }).pipe(takeUntil(this.reset$));
  }

  completeNavigation(): boolean {
    const completed = this.machine.completeNavigation();
    this.publish();
    return completed;
  }

  requestNavigation(
    direction: CollectionRevisionNavigationIntent,
  ): CollectionRevisionNavigationDecision {
    const decision = this.machine.requestNavigation(direction);
    this.publish();
    return decision;
  }

  requestPrimaryAction(): CollectionRevisionPrimaryActionDecision {
    const decision = this.machine.requestPrimaryAction();
    this.publish();
    return decision;
  }

  cancelPendingDecision(): boolean {
    const cancelled = this.machine.stay();
    this.publish();
    return cancelled;
  }

  discardPendingDecision(): CollectionRevisionNavigationDecision {
    const decision = this.machine.discardPendingDecision();
    this.publish();
    return decision;
  }

  completeCleanNavigation(
    direction: CollectionRevisionNavigationDirection,
    workflow: CollectionWorkflowDto,
  ): Observable<CollectionRevisionFileEditorCommitResult> {
    return defer(() => {
      const identity = this.requireIdentity();
      requireMatchingWorkflow(identity, workflow);
      if (
        !shouldCompleteRevisionLifecycle(direction, identity.targetId, workflow)
      ) {
        return of({
          direction,
          identity,
          workflow,
          authorization: null,
        });
      }

      const revisionEtag = this.machine.beginLifecycleCompletion();
      this.publish();
      return this.completeLifecycleIfRequired(
        direction,
        identity,
        workflow,
        revisionEtag,
      ).pipe(
        tap(({ workflow: completedWorkflow }) => {
          this.machine.completeLifecycleCompletion(completedWorkflow.etag);
          this.publish();
        }),
        map(({ workflow: completedWorkflow, authorization }) => ({
          direction,
          identity,
          workflow: completedWorkflow,
          authorization,
        })),
        catchError((error: unknown) => {
          if (this.state().busy) this.machine.failCommit();
          this.publish();
          return throwError(() => error);
        }),
      );
    }).pipe(takeUntil(this.reset$));
  }

  submitInitialFileSet(
    workflow: CollectionWorkflowDto,
  ): Observable<CollectionRevisionFileEditorCommitResult> {
    if (this.state().mode !== 'INITIAL_SUBMISSION') {
      return throwError(
        () => new Error('Initial submission requires a new target file set.'),
      );
    }
    return this.commitDesiredFileSet('NEXT', workflow);
  }

  confirmNavigationDecision(
    workflow: CollectionWorkflowDto,
  ): Observable<CollectionRevisionFileEditorCommitResult> {
    return defer(() => {
      const decision = this.state().pendingDecision;
      if (decision?.kind !== 'CONFIRM_UPDATE') {
        throw new Error(
          'No collection revision file update decision is pending.',
        );
      }
      return this.commitDesiredFileSet(decision.direction, workflow);
    }).pipe(takeUntil(this.reset$));
  }

  private commitDesiredFileSet(
    direction: CollectionRevisionNavigationIntent,
    workflow: CollectionWorkflowDto,
  ): Observable<CollectionRevisionFileEditorCommitResult> {
    return defer(() => {
      const identity = this.requireIdentity();
      requireMatchingWorkflow(identity, workflow);
      const intent = this.machine.beginCommit(direction);
      this.conflictIntent = intent;
      this.publish();
      return this.stageLocalFiles(identity, intent).pipe(
        switchMap((staged) => {
          this.machine.retainStagedFileIds(staged.map((file) => file.file_id));
          const fileIds = [
            ...intent.persistedFileIds,
            ...staged.map((file) => file.file_id),
          ];
          const request = this.toFileSetRequest(fileIds);
          this.machine.setCommitPhase('VALIDATING');
          this.publish();
          return this.revisionService
            .validateTargetFiles(
              identity.collectionId,
              identity.revisionId,
              identity.targetId,
              request,
            )
            .pipe(
              map((validation) => {
                this.machine.recordValidation(validation.files);
                this.publish();
                requireUsableExactValidation(request.files, validation);
                return request;
              }),
            );
        }),
        switchMap((request) => {
          this.machine.setCommitPhase('UPDATING');
          this.publish();
          return this.revisionService.commitTargetFileSet(
            identity.collectionId,
            identity.revisionId,
            identity.targetId,
            intent.targetEtag,
            request,
          );
        }),
        switchMap((versionedCommit) =>
          this.completeCommitResponse(direction, versionedCommit),
        ),
        catchError((error: unknown) => {
          if (error instanceof CollectionRevisionPreconditionConflict) {
            this.machine.enterConflict({
              current: error.current,
              targetEtag: error.targetEtag,
              revisionEtag: error.revisionEtag,
              traceId: error.traceId,
            });
          } else if (this.state().busy) {
            this.machine.failCommit(this.toFailure(error));
          }
          this.publish();
          return throwError(() => error);
        }),
      );
    }).pipe(takeUntil(this.reset$));
  }

  rebaseConflictAndRetry(
    workflow: CollectionWorkflowDto,
  ): Observable<CollectionRevisionFileEditorCommitResult> {
    return defer(() => {
      const conflict = this.state().conflict;
      const intent = this.conflictIntent;
      if (!conflict?.editable || !conflict.current.target_file_set || !intent) {
        throw new Error(
          'An editable file-set conflict is required for rebase.',
        );
      }
      const current = conflict.current.target_file_set;
      const stagedIds = new Map(
        [...this.stagedByClientId].map(([clientId, file]) => [
          clientId,
          file.file_id,
        ]),
      );
      const localDesiredIds = this.state().rows.flatMap((row) =>
        row.origin === 'PERSISTED'
          ? [row.fileId]
          : toPresentArray(stagedIds.get(row.clientId)),
      );
      const merged = mergeFileIds(
        this.state().baseFileIds,
        localDesiredIds,
        current.files.map((file) => file.file_id),
      );
      const identity = freezeIdentity({
        collectionId: current.collection_id,
        revisionId: current.revision_id,
        targetId: current.target_id,
      });
      this.identity = identity;
      requireMatchingWorkflow(identity, workflow);
      this.machine.beginConflictRetry(
        conflict.targetEtag,
        conflict.revisionEtag,
      );
      this.publish();

      return this.revisionService
        .validateTargetFiles(
          identity.collectionId,
          identity.revisionId,
          identity.targetId,
          this.toFileSetRequest(merged.desiredIds),
        )
        .pipe(
          map((validation) => {
            const request = this.toFileSetRequest(merged.desiredIds);
            this.machine.recordValidation(validation.files);
            this.publish();
            requireUsableExactValidation(request.files, validation);
            return request;
          }),
          switchMap((request) => {
            this.machine.setCommitPhase('UPDATING');
            this.publish();
            return this.revisionService.commitTargetFileSet(
              identity.collectionId,
              identity.revisionId,
              identity.targetId,
              conflict.targetEtag,
              request,
            );
          }),
          switchMap((commit) =>
            this.completeCommitResponse(intent.direction, commit),
          ),
          catchError((error: unknown) => {
            if (error instanceof CollectionRevisionPreconditionConflict) {
              this.machine.enterConflict({
                current: error.current,
                targetEtag: error.targetEtag,
                revisionEtag: error.revisionEtag,
                traceId: error.traceId,
              });
            } else if (this.state().busy) {
              this.machine.failCommit(this.toFailure(error));
            }
            this.publish();
            return throwError(() => error);
          }),
        );
    }).pipe(takeUntil(this.reset$));
  }

  discardConflictAndReload(): boolean {
    const conflict = this.state().conflict;
    const current = conflict?.current.target_file_set;
    if (!current) return false;
    this.identity = freezeIdentity({
      collectionId: current.collection_id,
      revisionId: current.revision_id,
      targetId: current.target_id,
    });
    this.stagedByClientId.clear();
    this.conflictIntent = null;
    const discarded = this.machine.discardConflictAndReload();
    this.publish();
    return discarded;
  }

  private completeCommitResponse(
    direction: CollectionRevisionNavigationIntent,
    versionedCommit: VersionedCollectionRevisionFileSetCommit,
  ): Observable<CollectionRevisionFileEditorCommitResult> {
    const effectiveIdentity = freezeIdentity({
      collectionId: versionedCommit.commit.navigation.collection_id,
      revisionId: versionedCommit.commit.navigation.revision_id,
      targetId: versionedCommit.commit.navigation.target_id,
    });
    this.machine.setCommitPhase('RELOADING');
    this.publish();
    return this.revisionService
      .getExactWorkflow(
        effectiveIdentity.collectionId,
        effectiveIdentity.revisionId,
      )
      .pipe(
        map((freshWorkflow) => {
          requireMatchingWorkflow(effectiveIdentity, freshWorkflow);
          return freshWorkflow;
        }),
        switchMap((freshWorkflow) =>
          this.completeLifecycleIfRequired(
            direction,
            effectiveIdentity,
            freshWorkflow,
            freshWorkflow.etag,
          ),
        ),
        tap(({ workflow: completedWorkflow }) => {
          this.identity = effectiveIdentity;
          this.machine.completeCommit({
            targetFileSet: {
              ...versionedCommit.commit.target_file_set,
              revision_status: completedWorkflow.revision_status,
              editable:
                completedWorkflow.editable &&
                versionedCommit.commit.target_file_set.editable,
              allowed_actions: completedWorkflow.editable
                ? versionedCommit.commit.target_file_set.allowed_actions
                : [],
            },
            targetEtag: versionedCommit.targetEtag,
            revisionEtag: completedWorkflow.etag,
          });
          this.stagedByClientId.clear();
          this.conflictIntent = null;
          this.publish();
        }),
        map(({ workflow: completedWorkflow, authorization }) => ({
          direction,
          identity: effectiveIdentity,
          workflow: completedWorkflow,
          authorization,
        })),
      );
  }

  private completeLifecycleIfRequired(
    direction: CollectionRevisionNavigationIntent,
    identity: CollectionRevisionTargetRouteIdentity,
    workflow: CollectionWorkflowDto,
    revisionEtag: string,
  ): Observable<{
    workflow: CollectionWorkflowDto;
    authorization: CollectionIntegrationAuthorization | null;
  }> {
    if (
      !shouldCompleteRevisionLifecycle(direction, identity.targetId, workflow)
    ) {
      return of({ workflow, authorization: null });
    }
    return this.lifecycleCoordinator.complete(
      identity,
      workflow,
      revisionEtag,
      this.integrationEnabled,
    );
  }

  private stageLocalFiles(
    identity: CollectionRevisionTargetRouteIdentity,
    intent: FrozenCollectionRevisionFileIntent,
  ): Observable<readonly StageTargetFileResponse[]> {
    if (intent.localFiles.length === 0) return of([]);
    const pending = intent.localFiles.filter(
      ({ clientId }) => !this.stagedByClientId.has(clientId),
    );
    const staged$ =
      pending.length === 0
        ? of([] as readonly StageTargetFileResponse[])
        : from(pending).pipe(
            mergeMap(
              ({ clientId, file }) =>
                this.uploadLocalFile(identity, clientId, file).pipe(
                  map((response) => ({
                    kind: 'STAGED' as const,
                    response,
                  })),
                  catchError((error: unknown) => {
                    this.machine.setLocalUploadState(
                      clientId,
                      'UPLOAD_FAILED',
                    );
                    this.publish();
                    return of({
                      kind: 'FAILED' as const,
                      failure: {
                        clientId,
                        fileName: file.name,
                        error,
                      },
                    });
                  }),
                ),
              4,
            ),
            toArray(),
            switchMap((results) => {
              const failures = results.flatMap((result) =>
                result.kind === 'FAILED' ? [result.failure] : [],
              );
              return failures.length > 0
                ? throwError(() => new CollectionRevisionFileUploadError(failures))
                : of(
                    results.flatMap((result) =>
                      result.kind === 'STAGED' ? [result.response] : [],
                    ),
                  );
            }),
          );
    return staged$.pipe(
      map(
        () =>
          intent.localFiles.map(({ clientId }) =>
            this.stagedByClientId.get(clientId),
          ) as StageTargetFileResponse[],
      ),
    );
  }

  private uploadLocalFile(
    identity: CollectionRevisionTargetRouteIdentity,
    clientId: string,
    file: File,
  ): Observable<StageTargetFileResponse> {
    return defer(() => {
      this.machine.setLocalUploadState(clientId, 'UPLOADING');
      this.publish();
      return this.revisionService.stageTargetFile(
        identity.collectionId,
        identity.revisionId,
        identity.targetId,
        file,
      );
    }).pipe(
      tap((response) => {
        this.stagedByClientId.set(clientId, response);
        this.machine.setLocalUploadState(clientId, 'STAGED');
        this.publish();
      }),
    );
  }

  private toFailure(error: unknown): CollectionRevisionFileEditorFailure {
    const uploadFailures =
      error instanceof CollectionRevisionFileUploadError
        ? error.failures
        : [];
    const uploadSessionExpired = uploadFailures.some(
      (failure) =>
        failure.error instanceof HttpErrorResponse &&
        failure.error.status === 401,
    );
    const kind =
      error instanceof CollectionRevisionFileValidationError
        ? 'VALIDATION'
        : uploadSessionExpired ||
            (error instanceof HttpErrorResponse && error.status === 401)
          ? 'SESSION'
          : this.state().phase === 'UPLOADING'
            ? 'UPLOAD'
            : 'COMMIT';
    const fileNames =
      error instanceof CollectionRevisionFileUploadError
        ? error.failures.map((failure) => failure.fileName)
        : error instanceof CollectionRevisionFileValidationError
        ? error.validation.files
            .filter((file) => !file.usable)
            .flatMap((file) => {
              const staged = [...this.stagedByClientId.entries()].find(
                ([, response]) => response.file_id === file.file_id,
              );
              if (staged) {
                const row = this.state().rows.find(
                  (candidate) =>
                    candidate.origin === 'LOCAL' &&
                    candidate.clientId === staged[0],
                );
                return row
                  ? [
                      row.name ||
                        (row.origin === 'PERSISTED'
                          ? row.fileId
                          : row.file.name),
                    ]
                  : [file.file_id];
              }
              const row = this.state().rows.find(
                (candidate) =>
                  candidate.origin === 'PERSISTED' &&
                  candidate.fileId === file.file_id,
              );
              return row
                ? [
                    row.name ||
                      (row.origin === 'PERSISTED' ? row.fileId : row.file.name),
                  ]
                : [file.file_id];
            })
        : [];
    return {
      kind,
      message: error instanceof Error ? error.message : 'Request failed.',
      fileNames,
      cause: error,
    };
  }

  private requireIdentity(): CollectionRevisionTargetRouteIdentity {
    if (!this.identity) {
      throw new Error('The collection revision file editor is not open.');
    }
    return this.identity;
  }

  private toFileSetRequest(fileIds: readonly string[]) {
    return {
      files: fileIds.map((fileId) => ({
        file_id: fileId,
        resolved_inputs: this.machine.resolvedInputsFor(fileId),
      })),
    };
  }

  private publish(): void {
    this.stateSignal.set(this.machine.snapshot());
  }
}

function toPresentArray(value: string | undefined): readonly string[] {
  return value ? [value] : [];
}

function requireUsableExactValidation(
  requestedFiles: readonly TargetFileIdRequest[],
  validation: CollectionRevisionValidationResponse,
): void {
  const returnedIds = validation.files.map((file) => file.file_id);
  const exact =
    returnedIds.length === requestedFiles.length &&
    returnedIds.every((fileId, index) => {
      const requested = requestedFiles[index];
      const returned = validation.files[index];
      return (
        fileId === requested.file_id &&
        sameResolvedInputs(requested.resolved_inputs, returned.resolved_inputs)
      );
    });
  if (
    !validation.all_usable ||
    !exact ||
    validation.files.some(
      (file) => !file.usable || file.required_inputs.length > 0,
    )
  ) {
    throw new CollectionRevisionFileValidationError(validation);
  }
}

function sameResolvedInputs(
  left: Readonly<Record<string, string>>,
  right: Readonly<Record<string, string>>,
): boolean {
  const leftEntries = Object.entries(left).sort(([a], [b]) =>
    a.localeCompare(b),
  );
  const rightEntries = Object.entries(right).sort(([a], [b]) =>
    a.localeCompare(b),
  );
  return (
    leftEntries.length === rightEntries.length &&
    leftEntries.every(
      ([key, value], index) =>
        rightEntries[index]?.[0] === key && rightEntries[index]?.[1] === value,
    )
  );
}

function freezeIdentity(
  identity: CollectionRevisionTargetRouteIdentity,
): CollectionRevisionTargetRouteIdentity {
  return Object.freeze({
    collectionId: requireNonEmpty(identity.collectionId, 'collectionId'),
    revisionId: requireNonEmpty(identity.revisionId, 'revisionId'),
    targetId: requireNonEmpty(identity.targetId, 'targetId'),
  });
}

function requireNonEmpty(value: string, field: string): string {
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new TypeError(`${field} must be a non-empty string`);
  }
  return value.trim();
}

function requireMatchingWorkflow(
  identity: CollectionRevisionTargetRouteIdentity,
  workflow: CollectionWorkflowDto,
): void {
  if (
    workflow.collection_id !== identity.collectionId ||
    workflow.revision_id !== identity.revisionId ||
    !workflow.targets.some((target) => target.target_id === identity.targetId)
  ) {
    throw new Error('Workflow does not match the committed revision target.');
  }
}

function shouldCompleteRevisionLifecycle(
  direction: CollectionRevisionNavigationIntent,
  currentTargetId: string,
  workflow: CollectionWorkflowDto,
): boolean {
  if (direction !== 'NEXT') {
    return false;
  }
  const orderedTargets = [...workflow.targets].sort(
    (left, right) => left.order_idx_snapshot - right.order_idx_snapshot,
  );
  const currentIndex = orderedTargets.findIndex(
    (target) => target.target_id === currentTargetId,
  );
  if (currentIndex < 0) {
    throw new Error(
      'The active target is absent from the collection workflow.',
    );
  }
  return (
    currentIndex === orderedTargets.length - 1 &&
    orderedTargets.every((target) => target.target_status === 'COMPLETED')
  );
}
